import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { teams, ads, AGE_GROUPS, DIVISIONS, LEVELS } from "./data/teams";
import AdBanner from "./components/AdBanner";
import TeamCard from "./components/TeamCard";
import TeamModal from "./components/TeamModal";
import ManagerForm from "./components/ManagerForm";
import PenaltyShootout from "./components/PenaltyShootout";
import SaveThePenalty from "./components/SaveThePenalty";
import FriendlyFinder from "./components/FriendlyFinder";
import AdminPanel from "./components/AdminPanel";

// ── Send tailored email via Supabase Edge Function ────────────────────────────
async function sendEmail(type, data) {
  try {
    await supabase.functions.invoke("send-email", { body: { type, data } });
  } catch (err) {
    console.error("Email send error:", err);
  }
}


// ── Simple form state helper ──────────────────────────────────────────────────
function useForm(initial) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return [form, set, setForm];
}

// ── Manager Sign Up Modal ────────────────────────────────────────────────────
function SignUpModal({ type, onClose }) {
  const isManager = type === "manager";
  const [form, set] = useForm({
    name: "", email: "", phone: "", club: "", faNumber: "", town: "", region: "",
    ageGroup: "", searchRadius: "town", notes: ""
  });
  const [done, setDone] = useState(false);
  const accent = isManager ? "#3b82f6" : "#10b981";
  const inputStyle = {
    width: "100%", background: "#1f2937", border: "1px solid #374151",
    borderRadius: 8, padding: "10px 12px", color: "#f3f4f6", fontSize: 13,
    outline: "none", boxSizing: "border-box", fontFamily: "inherit"
  };
  const labelStyle = { color: "#6b7280", fontSize: 11, display: "block", marginBottom: 5, letterSpacing: 1 };

  if (done) return (
    <div style={{ textAlign: "center", padding: "30px 10px" }}>
      <div style={{ fontSize: 60, marginBottom: 14 }}>{isManager ? "📋" : "🔔"}</div>
      <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 26, fontWeight: 700, marginBottom: 10 }}>
        {isManager ? "You're registered!" : "You're on the list!"}
      </div>
      <div style={{ color: "#9ca3af", fontSize: 14, lineHeight: 1.7, maxWidth: 340, margin: "0 auto 24px" }}>
        {isManager
          ? "You're all set! Head to '+ Post Free Listing' to get your team in front of parents and players — takes about 2 minutes."
          : "We'll email you as soon as a team in your area is looking for players in your child's age group. Keep an eye on your inbox!"}
      </div>
      <button onClick={onClose} style={{ background: accent, color: "#fff", border: "none", borderRadius: 10, padding: "12px 30px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>CLOSE</button>
    </div>
  );

  // ── Parent/Player — simple email notification capture ──
  if (!isManager) return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#10b98122", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🔔</div>
        <div>
          <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 20, fontWeight: 700 }}>Get Notified</div>
          <div style={{ color: "#6b7280", fontSize: 12 }}>Want to be found as a player? Get notified when a local team is looking</div>
        </div>
      </div>
      <div style={{ background: "#1f2937", borderRadius: 10, padding: "12px 14px", marginBottom: 16, borderLeft: "3px solid #10b981" }}>
        <div style={{ color: "#d1d5db", fontSize: 12, lineHeight: 1.6 }}>Want to be found as a player? Get notified when a local team is looking. Just leave your email and a couple of details — we'll do the rest. No account needed. No spam. Just a heads-up when the right team posts a listing.</div>
              <div style={{ color: "#f59e0b", fontSize: 12, marginTop: 8, padding: "8px 12px", background: "#f59e0b11", borderRadius: 8, border: "1px solid #f59e0b33" }}>
                📋 <strong>Are you a manager?</strong> Head to <strong>I'M A MANAGER</strong> instead to post a listing and find players.
              </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={labelStyle}>YOUR NAME</label>
            <input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="First name" />
          </div>
          <div>
            <label style={labelStyle}>EMAIL ADDRESS</label>
            <input style={inputStyle} value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@email.com" />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={labelStyle}>TOWN / CITY</label>
            <input style={inputStyle} value={form.town} onChange={e => set("town", e.target.value)} placeholder="e.g. Andover" />
          </div>
          <div>
            <label style={labelStyle}>AGE GROUP NEEDED</label>
            <select style={inputStyle} value={form.ageGroup} onChange={e => set("ageGroup", e.target.value)}>
              <option value="">Select...</option>
              {["U7","U8","U9","U10","U11","U12","U13","U14","U15","U16"].map(a => <option key={a}>{a}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={labelStyle}>HOW FAR SHOULD WE SEARCH?</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { value: "town", label: "📍 My town only" },
              { value: "county", label: "🗺️ My county" },
              { value: "anywhere", label: "🌍 Anywhere in the UK" }
            ].map(opt => (
              <button key={opt.value} onClick={() => set("searchRadius", opt.value)} type="button" style={{
                background: form.searchRadius === opt.value ? "#10b981" : "#1f2937",
                color: form.searchRadius === opt.value ? "#fff" : "#9ca3af",
                border: `1px solid ${form.searchRadius === opt.value ? "#10b981" : "#374151"}`,
                borderRadius: 20, padding: "7px 14px", fontSize: 12, fontWeight: 700,
                cursor: "pointer", fontFamily: "'Oswald', sans-serif", letterSpacing: 0.5,
                transition: "all 0.15s"
              }}>{opt.label}</button>
            ))}
          </div>
          <div style={{ color: "#4b5563", fontSize: 11, marginTop: 6 }}>
            {form.searchRadius === "town" && "You'll only be notified about teams in your town."}
            {form.searchRadius === "county" && "You'll be notified about teams anywhere in your county/region."}
            {form.searchRadius === "anywhere" && "You'll be notified about any team in the UK matching your age group."}
          </div>
        </div>
        <button onClick={async () => {
          const { error } = await supabase.from("notifications").insert([{ name: form.name, email: form.email, town: form.town, age_group: form.ageGroup, search_radius: form.searchRadius }]);
          if (error) { alert("Error: " + error.message); } else {
            sendEmail("notification", { name: form.name, email: form.email, town: form.town, age_group: form.ageGroup, search_radius: form.searchRadius });
            setDone(true);
          }
        }} style={{ background: "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "13px 0", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer", letterSpacing: 1, marginTop: 4 }}>
          NOTIFY ME ⚽
        </button>
        <div style={{ color: "#4b5563", fontSize: 11, textAlign: "center" }}>No account needed · No spam · Unsubscribe anytime</div>
      </div>
    </div>
  );

  // ── Manager — full registration ──
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#3b82f622", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📋</div>
        <div>
          <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 20, fontWeight: 700 }}>Manager Sign Up</div>
          <div style={{ color: "#6b7280", fontSize: 12 }}>List your team and find players</div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={labelStyle}>YOUR NAME</label>
            <input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="Full name" />
          </div>
          <div>
            <label style={labelStyle}>EMAIL ADDRESS</label>
            <input style={inputStyle} value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@email.com" />
          </div>
        </div>
        <div>
          <label style={labelStyle}>PHONE NUMBER (optional)</label>
          <input style={inputStyle} value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="07700 900000" />
        </div>
        <div>
          <label style={labelStyle}>CLUB NAME</label>
          <input style={inputStyle} value={form.club} onChange={e => set("club", e.target.value)} placeholder="e.g. Riverside Rangers Youth FC" />
        </div>
        <div>
          <label style={labelStyle}>FA AFFILIATION NUMBER <span style={{ color: "#ef4444" }}>*</span></label>
          <input style={inputStyle} value={form.faNumber} onChange={e => set("faNumber", e.target.value)} placeholder="e.g. 12345678" />
          <div style={{ color: "#4b5563", fontSize: 11, marginTop: 5, lineHeight: 1.5 }}>
            📋 Found on the FA Full-Time website or via your County FA. All listings are manually reviewed before going live.
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={labelStyle}>TOWN / CITY</label>
            <input style={inputStyle} value={form.town} onChange={e => set("town", e.target.value)} placeholder="e.g. Winchester" />
          </div>
          <div>
            <label style={labelStyle}>REGION / COUNTY</label>
            <input style={inputStyle} value={form.region} onChange={e => set("region", e.target.value)} placeholder="e.g. Hampshire" />
          </div>
        </div>
        <div>
          <label style={labelStyle}>ANYTHING ELSE? (optional)</label>
          <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 70 }} value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Tell us about your club..." />
        </div>
        <button onClick={async () => {
          const { error } = await supabase.from("managers").insert([{ name: form.name, email: form.email, phone: form.phone, club: form.club, fa_number: form.faNumber, town: form.town, region: form.region, notes: form.notes }]);
          if (error) { alert("Error: " + error.message); } else {
            sendEmail("manager", { name: form.name, email: form.email, club: form.club, town: form.town });
            setDone(true);
          }
        }} style={{ background: "#3b82f6", color: "#fff", border: "none", borderRadius: 10, padding: "13px 0", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer", letterSpacing: 1, marginTop: 4 }}>
          REGISTER AS MANAGER →
        </button>
      </div>
    </div>
  );
}

// ── Advertise Contact Form ────────────────────────────────────────────────────
function AdvertiseForm({ onClose }) {
  const [form, set] = useForm({ name: "", company: "", email: "", phone: "", budget: "", message: "" });
  const [done, setDone] = useState(false);
  const inputStyle = {
    width: "100%", background: "#1f2937", border: "1px solid #374151",
    borderRadius: 8, padding: "10px 12px", color: "#f3f4f6", fontSize: 13,
    outline: "none", boxSizing: "border-box", fontFamily: "inherit"
  };
  const labelStyle = { color: "#6b7280", fontSize: 11, display: "block", marginBottom: 5, letterSpacing: 1 };

  if (done) return (
    <div style={{ textAlign: "center", padding: "30px 10px" }}>
      <div style={{ fontSize: 60, marginBottom: 14 }}>🤝</div>
      <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 24, fontWeight: 700, marginBottom: 10 }}>Enquiry Received!</div>
      <div style={{ color: "#9ca3af", fontSize: 14, lineHeight: 1.7, maxWidth: 340, margin: "0 auto 24px" }}>
        Thanks for your interest in advertising with Grassrooting. We'll be in touch within 48 hours.
      </div>
      <button onClick={onClose} style={{ background: "#f59e0b", color: "#000", border: "none", borderRadius: 10, padding: "12px 30px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>CLOSE</button>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: "#f59e0b", fontFamily: "'Oswald', sans-serif", fontSize: 11, letterSpacing: 3, marginBottom: 4 }}>ADVERTISING ENQUIRY</div>
        <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Get in front of grassroots football families</div>
        <div style={{ color: "#6b7280", fontSize: 12, lineHeight: 1.6 }}>Kit brands, boot suppliers, coaching equipment, local businesses — reach thousands of engaged parents and players every week.</div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={labelStyle}>YOUR NAME</label>
            <input style={inputStyle} value={form.name} onChange={e => set("name", e.target.value)} placeholder="Full name" />
          </div>
          <div>
            <label style={labelStyle}>COMPANY / BRAND</label>
            <input style={inputStyle} value={form.company} onChange={e => set("company", e.target.value)} placeholder="e.g. ProDirect Soccer" />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={labelStyle}>EMAIL ADDRESS</label>
            <input style={inputStyle} value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@company.com" />
          </div>
          <div>
            <label style={labelStyle}>PHONE (optional)</label>
            <input style={inputStyle} value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="07700 900000" />
          </div>
        </div>
        <div>
          <label style={labelStyle}>MONTHLY BUDGET</label>
          <select style={inputStyle} value={form.budget} onChange={e => set("budget", e.target.value)}>
            <option value="">Select a range...</option>
            <option>Under £100/month</option>
            <option>£100 – £500/month</option>
            <option>£500 – £1,000/month</option>
            <option>£1,000+/month</option>
            <option>Let's discuss</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>TELL US ABOUT YOUR BRAND</label>
          <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} value={form.message} onChange={e => set("message", e.target.value)} placeholder="What are you looking to promote? Any specific age groups or regions you want to reach?" />
        </div>
        <button onClick={async () => {
          const { error } = await supabase.from("advertisers").insert([{ name: form.name, company: form.company, email: form.email, phone: form.phone, budget: form.budget, message: form.message }]);
          if (error) { alert("Error: " + error.message); } else {
            sendEmail("advertiser", { name: form.name, email: form.email, company: form.company, budget: form.budget });
            setDone(true);
          }
        }} style={{ background: "#f59e0b", color: "#000", border: "none", borderRadius: 10, padding: "13px 0", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer", letterSpacing: 1, marginTop: 4 }}>
          SEND ENQUIRY →
        </button>
      </div>
    </div>
  );
}

// ── Overlay Modal Wrapper ─────────────────────────────────────────────────────
function OverlayModal({ onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "#000000dd", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 18, padding: 28, maxWidth: 500, width: "100%", maxHeight: "90vh", overflowY: "auto", position: "relative" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 16, background: "#1f2937", border: "none", color: "#9ca3af", borderRadius: 8, padding: "4px 10px", cursor: "pointer", fontSize: 16 }}>✕</button>
        {children}
      </div>
    </div>
  );
}


// ── Unsubscribe Page ─────────────────────────────────────────────────────────
function UnsubscribePage({ email }) {
  const [status, setStatus] = useState("confirm"); // confirm | done | error

  const unsubscribe = async () => {
    const { error } = await supabase.from("notifications").delete().eq("email", email);
    if (error) setStatus("error");
    else setStatus("done");
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0d1117", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 16, padding: 32, maxWidth: 400, width: "100%", textAlign: "center" }}>
        {status === "confirm" && <>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
          <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Unsubscribe</div>
          <div style={{ color: "#9ca3af", fontSize: 14, marginBottom: 8, lineHeight: 1.6 }}>Are you sure you want to unsubscribe <strong style={{ color: "#f3f4f6" }}>{email}</strong> from Grassrooting notifications?</div>
          <div style={{ color: "#4b5563", fontSize: 12, marginBottom: 24 }}>You won't receive alerts when teams near you are looking for players.</div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button onClick={unsubscribe} style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>YES, UNSUBSCRIBE</button>
            <button onClick={() => window.location.href = "/"} style={{ background: "#1f2937", color: "#9ca3af", border: "none", borderRadius: 10, padding: "12px 24px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>CANCEL</button>
          </div>
        </>}
        {status === "done" && <>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Unsubscribed</div>
          <div style={{ color: "#9ca3af", fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>You've been removed from Grassrooting notifications. You won't receive any more emails.</div>
          <button onClick={() => window.location.href = "/"} style={{ background: "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "12px 24px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>BACK TO GRASSROOTING</button>
        </>}
        {status === "error" && <>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Something went wrong</div>
          <div style={{ color: "#9ca3af", fontSize: 14, marginBottom: 24 }}>Please email <strong style={{ color: "#10b981" }}>hello@grassrooting.co.uk</strong> to unsubscribe manually.</div>
        </>}
      </div>
    </div>
  );
}

// ── Privacy Policy Page ───────────────────────────────────────────────────────
function PrivacyPolicy({ onBack }) {
  const s = { color: "#9ca3af", fontSize: 13, lineHeight: 1.8, marginBottom: 16 };
  const h2 = { color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 700, marginTop: 28, marginBottom: 10 };
  const h3 = { color: "#10b981", fontFamily: "'Oswald', sans-serif", fontSize: 13, fontWeight: 700, letterSpacing: 2, marginBottom: 8, marginTop: 20 };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "24px 16px", fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <button onClick={onBack} style={{ background: "#1f2937", border: "none", color: "#9ca3af", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, marginBottom: 24, fontFamily: "inherit" }}>← Back</button>
      <div style={{ color: "#10b981", fontFamily: "'Oswald', sans-serif", fontSize: 11, letterSpacing: 3, marginBottom: 8 }}>GRASSROOTING</div>
      <h1 style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 32, fontWeight: 700, marginBottom: 4 }}>Privacy Policy</h1>
      <p style={{ color: "#4b5563", fontSize: 12, marginBottom: 32 }}>Last updated: June 2026</p>

      <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 14, padding: "20px 24px", marginBottom: 24 }}>
        <p style={s}>Grassrooting ("we", "us", "our") is a UK-based platform connecting grassroots youth football clubs with parents and players. We operate at <strong style={{ color: "#f3f4f6" }}>grassrooting.co.uk</strong>. For the purposes of UK GDPR, Grassrooting is the data controller responsible for your personal data.</p>
        <p style={{ ...s, marginBottom: 0 }}>📧 Contact: <strong style={{ color: "#10b981" }}>hello@grassrooting.co.uk</strong></p>
      </div>

      <h2 style={h2}>1. What Data We Collect</h2>
      <h3 style={h3}>PARENTS & PLAYERS (GET NOTIFIED)</h3>
      <p style={s}>Full name, email address, town/city, child's age group, notification preference (location radius).</p>
      <h3 style={h3}>MANAGERS & COACHES (POST A LISTING)</h3>
      <p style={s}>Full name, email address, phone number (optional), club name, FA Affiliation Number, town/city and region, league and division, club badge image URL (optional).</p>
      <h3 style={h3}>FRIENDLY FINDER</h3>
      <p style={s}>Club name, contact email, town/city and region, match details (date, surface, age group).</p>
      <h3 style={h3}>ADVERTISER ENQUIRIES</h3>
      <p style={s}>Full name, company/brand name, email address, phone number (optional), budget range, message content.</p>

      <h2 style={h2}>2. How We Use Your Data</h2>
      <p style={s}>We use your data to: send email notifications when a matching team posts a listing; send confirmation emails when you submit a form; review and approve manager listings; respond to advertiser enquiries; and maintain records for platform operation. We will <strong style={{ color: "#f3f4f6" }}>never sell your personal data</strong> to third parties.</p>

      <h2 style={h2}>3. Who We Share Your Data With</h2>
      <p style={s}>We use the following trusted third-party services:</p>
      <div style={{ background: "#1f2937", borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}>
        {[
          ["Supabase (supabase.com)", "Secure database storage — EU (Ireland region), GDPR compliant"],
          ["Resend (resend.com)", "Email delivery service — GDPR compliant"],
          ["Netlify (netlify.com)", "Website hosting — GDPR compliant"],
        ].map(([name, desc]) => (
          <div key={name} style={{ display: "flex", gap: 12, marginBottom: 10, alignItems: "flex-start" }}>
            <span style={{ color: "#10b981", fontSize: 12, marginTop: 1 }}>✓</span>
            <div>
              <div style={{ color: "#f3f4f6", fontSize: 13, fontWeight: 600 }}>{name}</div>
              <div style={{ color: "#6b7280", fontSize: 12 }}>{desc}</div>
            </div>
          </div>
        ))}
      </div>

      <h2 style={h2}>4. How Long We Keep Your Data</h2>
      <div style={{ background: "#1f2937", borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}>
        {[
          ["Parent/player sign-ups", "Until you unsubscribe or request deletion"],
          ["Manager registrations", "Until you request deletion"],
          ["Team listings", "Hidden after 30 days, data retained for platform records"],
          ["Friendly requests", "Until the match date has passed"],
          ["Advertiser enquiries", "12 months"],
        ].map(([type, period]) => (
          <div key={type} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #374151", gap: 12 }}>
            <span style={{ color: "#d1d5db", fontSize: 12 }}>{type}</span>
            <span style={{ color: "#9ca3af", fontSize: 12, textAlign: "right" }}>{period}</span>
          </div>
        ))}
      </div>

      <h2 style={h2}>5. Your Rights</h2>
      <p style={s}>Under UK GDPR you have the right to: access your data; correct inaccurate data; request deletion ("right to be forgotten"); restrict processing; object to processing; and data portability. To exercise any right, email <strong style={{ color: "#10b981" }}>hello@grassrooting.co.uk</strong>. We will respond within 30 days.</p>

      <h2 style={h2}>6. Email Communications</h2>
      <p style={s}>When you submit a form you may receive confirmation emails and (for parents/players) notification emails when matching teams post listings. To unsubscribe, email <strong style={{ color: "#10b981" }}>hello@grassrooting.co.uk</strong> with the subject "Unsubscribe" and we'll remove you within 48 hours.</p>

      <h2 style={h2}>7. Children's Data</h2>
      <p style={s}>Grassrooting is for grassroots youth football (U7–U16). We collect limited data about children (age group only) as part of parent/player sign-ups. We do not directly collect personal data from children. Parents and guardians are responsible for submitting information on behalf of their children.</p>

      <h2 style={h2}>8. Data Security</h2>
      <p style={s}>Your data is stored securely on Supabase's encrypted EU servers, accessed only by authorised Grassrooting administrators, protected by Row Level Security (RLS) policies, and transmitted over HTTPS (encrypted connections only).</p>

      <h2 style={h2}>9. Cookies</h2>
      <p style={s}>Grassrooting currently does not use tracking or advertising cookies. We may use essential cookies to ensure the platform functions correctly. If this changes, we will update this policy.</p>

      <h2 style={h2}>10. Complaints</h2>
      <p style={s}>If you are unhappy with how we handle your data, you can lodge a complaint with the <strong style={{ color: "#f3f4f6" }}>Information Commissioner's Office (ICO)</strong> at ico.org.uk or call 0303 123 1113.</p>

      <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 12, padding: "20px 24px", marginTop: 32 }}>
        <div style={{ color: "#10b981", fontFamily: "'Oswald', sans-serif", fontSize: 11, letterSpacing: 2, marginBottom: 6 }}>CONTACT US</div>
        <p style={{ ...s, marginBottom: 0 }}>Questions about this policy? Email <strong style={{ color: "#10b981" }}>hello@grassrooting.co.uk</strong> or visit <strong style={{ color: "#10b981" }}>grassrooting.co.uk</strong></p>
      </div>
    </div>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer({ onPrivacy }) {
  return (
    <div style={{ background: "#0d1117", borderTop: "1px solid #1f2937", padding: "20px 16px", marginTop: 40, textAlign: "center" }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <div style={{ marginBottom: 10 }}>
          <span style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 16, fontWeight: 700 }}>GRASS</span>
          <span style={{ color: "#10b981", fontFamily: "'Oswald', sans-serif", fontSize: 16, fontWeight: 700 }}>ROOTING</span>
        </div>
        <div style={{ color: "#4b5563", fontSize: 11, letterSpacing: 2, marginBottom: 12 }}>THE UK'S HOME FOR GRASSROOTS FOOTBALL</div>
        <div style={{ display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap" }}>
          <button onClick={onPrivacy} style={{ background: "none", border: "none", color: "#6b7280", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>Privacy Policy</button>
          <a href="mailto:hello@grassrooting.co.uk" style={{ color: "#6b7280", fontSize: 12, textDecoration: "underline" }}>hello@grassrooting.co.uk</a>
          <span style={{ color: "#4b5563", fontSize: 12 }}>© 2026 Grassrooting</span>
        </div>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const isAdmin = typeof window !== "undefined" && window.location.hash === "#admin";
  const unsubEmail = typeof window !== "undefined" && window.location.hash.startsWith("#unsubscribe") 
    ? new URLSearchParams(window.location.hash.replace("#unsubscribe?", "")).get("email") 
    : null;
  const [view, setView] = useState("browse");
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [gameMode, setGameMode] = useState("shootout"); // "shootout" | "keeper"
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showManagerForm, setShowManagerForm] = useState(false);
  const [modal, setModal] = useState(null); // "manager-signup" | "parent-signup" | "advertise"
  const [filters, setFilters] = useState({ ageGroup: "All", day: "All", division: "All", level: "All", search: "" });
  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const [liveListings, setLiveListings] = useState([]);

  useEffect(() => {
    const fetchListings = async () => {
      const { data } = await supabase.from("listings").select("*").eq("status", "approved").order("created_at", { ascending: false });
      if (data) {
        const mapped = data.map(item => ({
          id: `live-${item.id}`,
          club: item.club,
          league: item.league || "",
          division: item.division || "",
          day: item.day || "",
          ageGroup: item.age_group || "",
          region: item.region || "",
          town: item.town || "",
          level: item.level || "",
          levelDesc: item.level_desc || "",
          positions: item.positions || [],
          contact: item.contact_email || "",
          phone: item.phone || "",
          posted: new Date(item.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
          badge: "⚽",
          badge_url: item.badge_url || null,
          colour: "#10b981",
        }));
        setLiveListings(mapped);
      }
    };
    fetchListings();
  }, []);

  const allTeams = [...liveListings, ...teams];

  const filtered = allTeams.filter(t => {
    if (filters.ageGroup !== "All" && t.ageGroup !== filters.ageGroup) return false;
    if (filters.day !== "All" && t.day !== filters.day) return false;
    if (filters.division !== "All" && t.division !== filters.division) return false;
    if (filters.level !== "All" && t.level !== filters.level) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      if (!t.club.toLowerCase().includes(q) && !t.town.toLowerCase().includes(q) && !t.ageGroup.toLowerCase().includes(q) && !t.region.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const btn = (active, colour) => ({
    background: active ? (colour || "#10b981") : "#1f2937",
    color: active ? "#fff" : "#9ca3af",
    border: "none", borderRadius: 20, padding: "6px 14px", fontSize: 12,
    fontWeight: 700, cursor: "pointer", fontFamily: "'Oswald', sans-serif",
    letterSpacing: 0.5, transition: "all 0.15s",
  });

  const navBtn = (v, label) => (
    <button onClick={() => setView(v)} style={btn(view === v)}>
      {label}
    </button>
  );

  if (unsubEmail) {
    return <UnsubscribePage email={unsubEmail} />;
  }

  if (showPrivacy) {
    return (
      <div style={{ minHeight: "100vh", background: "#0d1117" }}>
        <PrivacyPolicy onBack={() => setShowPrivacy(false)} />
        <Footer onPrivacy={() => setShowPrivacy(true)} />
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div style={{ minHeight: "100vh", background: "#0d1117", fontFamily: "'Inter','Segoe UI',sans-serif", padding: "24px 16px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <AdminPanel />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0d1117", fontFamily: "'Inter','Segoe UI',sans-serif" }}>

      {/* ── HEADER ── */}
      <div style={{ background: "#0d1117", borderBottom: "1px solid #1f2937", padding: "0 20px", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 62, padding: "10px 0", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 28 }}>⚽</span>
            <div>
              <span style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 1 }}>GRASS</span>
              <span style={{ color: "#10b981", fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: 1 }}>ROOTING</span>
              <div style={{ color: "#10b981", fontSize: 9, letterSpacing: 2, marginTop: -2 }}>THE UK'S HOME FOR GRASSROOTS FOOTBALL</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {navBtn("browse", "🔍 FIND A TEAM")}
            {navBtn("manager", "📋 I'M A MANAGER")}
            {navBtn("friendly", "🤝 FRIENDLY FINDER")}
            {navBtn("game", "🎮 PLAY")}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>

        {/* ── BROWSE VIEW ── */}
        {view === "browse" && (
          <>
            {/* Hero */}
            <div style={{ background: "linear-gradient(135deg,#0f2027 0%,#1a3a2a 60%,#0d1117 100%)", borderRadius: 18, padding: "30px 28px", marginBottom: 24, border: "1px solid #1f2937", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", right: -20, top: -20, width: 200, height: 200, background: "#10b98115", borderRadius: "50%", filter: "blur(40px)" }} />
              <div style={{ color: "#10b981", fontFamily: "'Oswald', sans-serif", fontSize: 11, letterSpacing: 3, marginBottom: 8 }}>GRASSROOTS YOUTH FOOTBALL · WORLDWIDE</div>
              <h1 style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 30, fontWeight: 700, margin: "0 0 8px", lineHeight: 1.1 }}>Find your team.<br />Find your player.</h1>
              <p style={{ color: "#9ca3af", fontSize: 13, margin: "0 0 20px", maxWidth: 440, lineHeight: 1.6 }}>The UK hub for grassroots youth football. Clubs and parents in one place — no more missed Facebook posts. U7 to U16, Saturday & Sunday, every league, everywhere.</p>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                <button onClick={() => setModal("parent-signup")} style={{ background: "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "11px 20px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", letterSpacing: 1 }}>👨‍👩‍👧 GET NOTIFIED ⚽</button>
                <button onClick={() => setModal("manager-signup")} style={{ background: "#3b82f622", color: "#93c5fd", border: "1px solid #3b82f644", borderRadius: 10, padding: "11px 20px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", letterSpacing: 1 }}>📋 MANAGER SIGN UP</button>
              </div>
            </div>

            <AdBanner ad={ads[0]} />

            {/* Example notice */}
            <div style={{ background: "#1f2937", border: "1px dashed #10b981", borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 22 }}>💡</span>
              <div>
                <div style={{ color: "#10b981", fontFamily: "'Oswald', sans-serif", fontSize: 13, fontWeight: 700, marginBottom: 2 }}>EXAMPLE LISTING BELOW</div>
                <div style={{ color: "#9ca3af", fontSize: 12, lineHeight: 1.5 }}>This is what a team listing looks like on Grassrooting. Managers — sign up and post your team in minutes. Parents — sign up to get notified when teams near you are looking.</div>
              </div>
            </div>

            {/* Search */}
            <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
              <input value={filters.search} onChange={e => setFilter("search", e.target.value)} style={{ width: "100%", background: "transparent", border: "none", color: "#f3f4f6", fontSize: 15, outline: "none", boxSizing: "border-box" }} placeholder="🔍  Search by club, town, region, or age group..." />
            </div>

            {/* Filters */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ color: "#4b5563", fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>AGE GROUP</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {AGE_GROUPS.map(a => <button key={a} onClick={() => setFilter("ageGroup", a)} style={btn(filters.ageGroup === a)}>{a}</button>)}
                </div>
              </div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <div style={{ color: "#4b5563", fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>DAY</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {["All","Saturday","Sunday"].map(d => <button key={d} onClick={() => setFilter("day", d)} style={btn(filters.day === d)}>{d}</button>)}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#4b5563", fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>DIVISION</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {DIVISIONS.map(d => <button key={d} onClick={() => setFilter("division", d)} style={btn(filters.division === d)}>{d}</button>)}
                  </div>
                </div>
                <div>
                  <div style={{ color: "#4b5563", fontSize: 10, letterSpacing: 2, marginBottom: 8 }}>LEVEL</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {LEVELS.map(l => <button key={l} onClick={() => setFilter("level", l)} style={btn(filters.level === l)}>{l}</button>)}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ color: "#4b5563", fontSize: 12, marginBottom: 14 }}>{filtered.length} team listing{filtered.length !== 1 ? "s" : ""} currently active</div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 14 }}>
              {filtered.map(team => <TeamCard key={team.id} team={team} onOpen={setSelectedTeam} />)}
            </div>

            {filtered.length === 0 && (
              <div style={{ textAlign: "center", color: "#4b5563", padding: 40, fontSize: 14 }}>No teams match your filters — try broadening your search.</div>
            )}

            <div style={{ marginTop: 24 }}><AdBanner ad={ads[1]} /></div>

            {/* Sign up CTA strip */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 24 }}>
              <div style={{ background: "linear-gradient(135deg,#0f1f3a,#0d1117)", border: "1px solid #3b82f633", borderRadius: 14, padding: "22px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                <div style={{ color: "#93c5fd", fontFamily: "'Oswald', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 6 }}>MANAGER?</div>
                <div style={{ color: "#6b7280", fontSize: 12, lineHeight: 1.5, marginBottom: 16 }}>Post your team and reach parents looking right now.</div>
                <button onClick={() => setModal("manager-signup")} style={{ background: "#3b82f6", color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", width: "100%" }}>GET NOTIFIED FREE →</button>
              </div>
              <div style={{ background: "linear-gradient(135deg,#0f2027,#0d1117)", border: "1px solid #10b98133", borderRadius: 14, padding: "22px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>👨‍👩‍👧</div>
                <div style={{ color: "#10b981", fontFamily: "'Oswald', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 6 }}>PARENT OR PLAYER?</div>
                <div style={{ color: "#6b7280", fontSize: 12, lineHeight: 1.5, marginBottom: 16 }}>Leave your email — we'll alert you when a team near you is looking for players.</div>
                <button onClick={() => setModal("parent-signup")} style={{ background: "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", width: "100%" }}>GET NOTIFIED FREE →</button>
              </div>
            </div>
          </>
        )}

        {/* ── MANAGER VIEW ── */}
        {view === "manager" && (
          <>
            <div style={{ background: "linear-gradient(135deg,#0f1f2a 0%,#0d1117 100%)", borderRadius: 18, padding: "28px", marginBottom: 24, border: "1px solid #1f2937" }}>
              <div style={{ color: "#3b82f6", fontFamily: "'Oswald', sans-serif", fontSize: 12, letterSpacing: 3, marginBottom: 8 }}>MANAGER DASHBOARD</div>
              <h2 style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 26, fontWeight: 700, margin: "0 0 8px" }}>List your team for free</h2>
              <p style={{ color: "#9ca3af", fontSize: 13, margin: "0 0 20px", lineHeight: 1.6 }}>Reach parents and players wherever you are — locally, nationally, or globally. No more hoping they stumble across your Facebook post.</p>
              <button onClick={() => setShowManagerForm(true)} style={{ background: "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "11px 22px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer", letterSpacing: 1 }}>+ POST YOUR TEAM FREE ⚽</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12, marginBottom: 24 }}>
              {[
                ["📣", "Reach More Players", "Your listing is seen by parents actively looking — not just those who stumble across a Facebook group."],
                ["🎯", "Right Age & Area", "Parents can filter by age group, division, and location to find your team instantly."],
                ["⚡", "Live in Minutes", "Free to post. Your listing goes live straight away."],
                ["🔔", "Parents Are Waiting", "Registered parents get notified when a team matching their child's age and area goes live."]
              ].map(([icon, title, desc]) => (
                <div key={title} style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 12, padding: "18px 16px" }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
                  <div style={{ color: "#f3f4f6", fontFamily: "'Oswald', sans-serif", fontWeight: 700, marginBottom: 6 }}>{title}</div>
                  <div style={{ color: "#6b7280", fontSize: 12, lineHeight: 1.5 }}>{desc}</div>
                </div>
              ))}
            </div>

            <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 14, padding: "24px" }}>
              <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 20 }}>
                {showManagerForm ? "📋 POST A PLAYER WANTED LISTING" : "Ready to post a listing?"}
              </div>
              {!showManagerForm
                ? <button onClick={() => setShowManagerForm(true)} style={{ background: "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "14px 28px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer", letterSpacing: 1 }}>+ POST YOUR TEAM FREE ⚽</button>
                : <ManagerForm onClose={() => setShowManagerForm(false)} />
              }
            </div>

            <AdBanner ad={ads[2]} />

            {/* Advertise section */}
            <div style={{ background: "#111827", border: "1px solid #f59e0b33", borderRadius: 14, padding: "24px", marginTop: 16 }}>
              <div style={{ color: "#f59e0b", fontFamily: "'Oswald', sans-serif", fontSize: 13, letterSpacing: 2, marginBottom: 8 }}>ADVERTISE WITH GRASSROOTING</div>
              <div style={{ color: "#f3f4f6", fontWeight: 600, fontSize: 16, marginBottom: 8 }}>Reach grassroots football families across the UK and beyond</div>
              <div style={{ color: "#6b7280", fontSize: 13, lineHeight: 1.6, marginBottom: 18 }}>Kit suppliers, boot brands, coaching equipment, and football services — put your brand in front of thousands of engaged football parents and players every week, wherever they are.</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 10, marginBottom: 20 }}>
                {[["👕","Kit & Apparel"],["👟","Boots & Footwear"],["🥅","Goals & Equipment"],["📱","Apps & Tech"],["🏫","Coaching Courses"],["🏆","Tournaments"]].map(([icon, label]) => (
                  <div key={label} style={{ background: "#1f2937", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 18 }}>{icon}</span>
                    <span style={{ color: "#d1d5db", fontSize: 12, fontWeight: 600 }}>{label}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => setModal("advertise")} style={{ background: "#f59e0b", color: "#000", border: "none", borderRadius: 10, padding: "13px 28px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer", letterSpacing: 1 }}>GET IN TOUCH →</button>
            </div>
          </>
        )}

        {/* ── FRIENDLY FINDER VIEW ── */}
        {view === "friendly" && (
          <FriendlyFinder />
        )}

        {/* ── GAME VIEW ── */}
        {view === "game" && (
          <div style={{ margin: "0 -16px" }}>
            <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: "16px 16px 0" }}>
              <button onClick={() => setGameMode("shootout")} style={{
                background: gameMode === "shootout" ? "#10b981" : "#1f2937",
                color: gameMode === "shootout" ? "#fff" : "#9ca3af",
                border: "none", borderRadius: 20, padding: "8px 18px", fontSize: 12,
                fontWeight: 700, fontFamily: "'Oswald', sans-serif", letterSpacing: 0.5, cursor: "pointer"
              }}>
                ⚽ PENALTY SHOOTOUT
              </button>
              <button onClick={() => setGameMode("keeper")} style={{
                background: gameMode === "keeper" ? "#3b82f6" : "#1f2937",
                color: gameMode === "keeper" ? "#fff" : "#9ca3af",
                border: "none", borderRadius: 20, padding: "8px 18px", fontSize: 12,
                fontWeight: 700, fontFamily: "'Oswald', sans-serif", letterSpacing: 0.5, cursor: "pointer"
              }}>
                🧤 SAVE THE PENALTY
              </button>
            </div>
            {gameMode === "shootout" ? <PenaltyShootout /> : <SaveThePenalty />}
          </div>
        )}
      </div>

      <Footer onPrivacy={() => setShowPrivacy(true)} />

      {/* ── MODALS ── */}
      {selectedTeam && <TeamModal team={selectedTeam} onClose={() => setSelectedTeam(null)} />}

      {modal === "manager-signup" && (
        <OverlayModal onClose={() => setModal(null)}>
          <SignUpModal type="manager" onClose={() => setModal(null)} />
        </OverlayModal>
      )}
      {modal === "parent-signup" && (
        <OverlayModal onClose={() => setModal(null)}>
          <SignUpModal type="parent" onClose={() => setModal(null)} />
        </OverlayModal>
      )}
      {modal === "advertise" && (
        <OverlayModal onClose={() => setModal(null)}>
          <AdvertiseForm onClose={() => setModal(null)} />
        </OverlayModal>
      )}
    </div>
  );
}
